import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WeekMenu, LunchCard, CompanyCard, sampleMenus, MOCK_CARD, MOCK_COMPANY_CARD } from '@/lib/lunchData';
import { fetchMenus, saveMenu } from '@/lib/menuService';
import { getCurrentWeekInfo, WeekInfo } from '@/lib/dateUtils';
import { normalizeSwedishPhone } from '@/lib/phoneUtils';

interface AppContextType {
  menus: WeekMenu[];
  setMenus: React.Dispatch<React.SetStateAction<WeekMenu[]>>;
  lunchCards: LunchCard[];
  setLunchCards: React.Dispatch<React.SetStateAction<LunchCard[]>>;
  companyCards: CompanyCard[];
  setCompanyCards: React.Dispatch<React.SetStateAction<CompanyCard[]>>;
  currentWeekInfo: WeekInfo;
  addLunchCard: (phoneNumber: string) => LunchCard;
  addCompanyCard: (data: Omit<CompanyCard, 'id' | 'purchaseDate'>) => CompanyCard;
  useLunch: (cardId: string) => boolean;
  useCompanyLunch: (cardId: string) => boolean;
  findCardByPhone: (phone: string) => LunchCard | undefined;
  findCompanyCardByPhone: (phone: string) => CompanyCard | undefined;
  getMenuForWeek: (week: number, year: number) => WeekMenu | undefined;
  updateMenu: (menu: WeekMenu) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initializeLunchCards = (): LunchCard[] => {
  const saved = localStorage.getItem('lunchCards');
  if (saved) {
    const cards: LunchCard[] = JSON.parse(saved);
    const hasMockCard = cards.some(c => c.id === MOCK_CARD.id);
    if (!hasMockCard) {
      return [MOCK_CARD, ...cards];
    }
    return cards;
  }
  return [MOCK_CARD];
};

const initializeCompanyCards = (): CompanyCard[] => {
  const saved = localStorage.getItem('companyCards');
  if (saved) {
    const cards: CompanyCard[] = JSON.parse(saved);
    const hasMockCard = cards.some(c => c.id === MOCK_COMPANY_CARD.id);
    if (!hasMockCard) {
      return [MOCK_COMPANY_CARD, ...cards];
    }
    return cards;
  }
  return [MOCK_COMPANY_CARD];
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [menus, setMenus] = useState<WeekMenu[]>(sampleMenus);

  useEffect(() => {
    const loadMenus = async () => {
      const fetchedMenus = await fetchMenus();
      if (fetchedMenus.length > 0) {
        setMenus(fetchedMenus);
      }
    };
    loadMenus();
  }, []);

  const [lunchCards, setLunchCards] = useState<LunchCard[]>(initializeLunchCards);
  const [companyCards, setCompanyCards] = useState<CompanyCard[]>(initializeCompanyCards);

  const currentWeekInfo = getCurrentWeekInfo();



  useEffect(() => {
    localStorage.setItem('lunchCards', JSON.stringify(lunchCards));
  }, [lunchCards]);

  useEffect(() => {
    localStorage.setItem('companyCards', JSON.stringify(companyCards));
  }, [companyCards]);

  const addLunchCard = (phoneNumber: string): LunchCard => {
    const normalized = normalizeSwedishPhone(phoneNumber);
    if (!normalized) {
      throw new Error('Ogiltigt telefonnummer');
    }
    
    const newCard: LunchCard = {
      id: `card-${Date.now()}`,
      phoneNumber: normalized,
      remaining: 10,
      purchaseDate: new Date().toISOString(),
    };
    setLunchCards(prev => [...prev, newCard]);
    return newCard;
  };

  const addCompanyCard = (data: Omit<CompanyCard, 'id' | 'purchaseDate'>): CompanyCard => {
    const normalized = normalizeSwedishPhone(data.phoneNumber);
    if (!normalized) {
      throw new Error('Ogiltigt telefonnummer');
    }
    
    const newCard: CompanyCard = {
      ...data,
      id: `company-${Date.now()}`,
      phoneNumber: normalized,
      purchaseDate: new Date().toISOString(),
    };
    setCompanyCards(prev => [...prev, newCard]);
    return newCard;
  };

  const useLunch = (cardId: string): boolean => {
    let success = false;
    setLunchCards(prev =>
      prev.map(card => {
        if (card.id === cardId && card.remaining > 0) {
          success = true;
          return { ...card, remaining: card.remaining - 1 };
        }
        return card;
      })
    );
    return success;
  };

  const useCompanyLunch = (cardId: string): boolean => {
    let success = false;
    setCompanyCards(prev =>
      prev.map(card => {
        if (card.id === cardId && card.remaining > 0) {
          success = true;
          return { ...card, remaining: card.remaining - 1 };
        }
        return card;
      })
    );
    return success;
  };

  const findCardByPhone = (phone: string): LunchCard | undefined => {
    const normalized = normalizeSwedishPhone(phone);
    if (!normalized) {
      const cleaned = phone.replace(/\D/g, '');
      return lunchCards.find(card => card.phoneNumber === cleaned);
    }
    return lunchCards.find(card => card.phoneNumber === normalized);
  };

  const findCompanyCardByPhone = (phone: string): CompanyCard | undefined => {
    const normalized = normalizeSwedishPhone(phone);
    if (!normalized) {
      const cleaned = phone.replace(/\D/g, '');
      return companyCards.find(card => card.phoneNumber === cleaned);
    }
    return companyCards.find(card => card.phoneNumber === normalized);
  };

  const getMenuForWeek = (week: number, year: number): WeekMenu | undefined => {
    return menus.find(m => m.week === week && m.year === year);
  };

  const updateMenu = async (menu: WeekMenu) => {
    setMenus(prev => {
      const existing = prev.findIndex(m => m.week === menu.week && m.year === menu.year);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = menu;
        return updated;
      }
      return [...prev, menu];
    });

    await saveMenu(menu);
  };

  return (
    <AppContext.Provider
      value={{
        menus,
        setMenus,
        lunchCards,
        setLunchCards,
        companyCards,
        setCompanyCards,
        currentWeekInfo,
        addLunchCard,
        addCompanyCard,
        useLunch,
        useCompanyLunch,
        findCardByPhone,
        findCompanyCardByPhone,
        getMenuForWeek,
        updateMenu,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
